'use client'

import { forwardRef, useState, useEffect, useRef, useCallback } from 'react'
import { AlertCircle, CheckCircle2, Info, RotateCcw } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS Textarea Component
 * 
 * Enhanced textarea implementing all 10 BIRHAUS principles:
 * - Spanish-first labeling and validation messages
 * - Progressive validation with cognitive load reduction
 * - Auto-save functionality with visual feedback
 * - Accessibility compliance (WCAG AA+)
 * - Character counting with Miller's Law guidance
 * - Undo functionality for large text changes
 * - Auto-resize option for better UX
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

interface UndoState {
  previousValue: string
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

export interface BirhausTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'autoSave'> {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Spanish-first error patterns: "Qué pasó + cómo resolver"
  solution?: string
  solutionEs?: string
  solutionEn?: string
  
  // BIRHAUS Principle #8: Performance is UX
  autoSave?: boolean
  autoSaveDelay?: number
  onAutoSave?: (value: string) => Promise<void>
  
  // Progressive validation
  validateOnChange?: boolean
  validateOnBlur?: boolean
  validationRules?: ValidationRule[]
  
  // BIRHAUS Principle #1: Form serves flow
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'filled' | 'minimal'
  
  // Character management (Miller's Law)
  maxLength?: number
  showCharacterCount?: boolean
  characterCountThreshold?: number // Show warning when approaching limit
  
  // Auto-resize functionality
  autoResize?: boolean
  minRows?: number
  maxRows?: number
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  undoThreshold?: number // Minimum chars changed to trigger undo
  
  // BIRHAUS Principle #6: Accessibility = dignity
  helpText?: string
  helpTextEs?: string
  helpTextEn?: string
  
  // Status indicators
  loading?: boolean
  success?: boolean
  
  // Cognitive load tracking
  fieldPriority?: 'required' | 'optional' | 'progressive'
  cognitiveWeight?: number
  
  // Locale
  locale?: 'es' | 'en'
}

export interface ValidationRule {
  test: (value: string) => boolean
  errorEs: string
  errorEn: string
  solution?: string
  solutionEs?: string
  solutionEn?: string
}

export const BirhausTextarea = forwardRef<HTMLTextAreaElement, BirhausTextareaProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // Validation
  error,
  errorEs: _errorEs,
  errorEn: _errorEn,
  solution,
  solutionEs,
  solutionEn,
  
  // Auto-save
  autoSave = false,
  autoSaveDelay = 2000,
  onAutoSave,
  
  // Progressive validation
  validateOnChange = false,
  validateOnBlur = true,
  validationRules = [],
  
  // Appearance
  size = 'md',
  variant = 'default',
  
  // Character management
  maxLength,
  showCharacterCount = false,
  characterCountThreshold = 0.8,
  
  // Auto-resize
  autoResize = false,
  minRows = 3,
  maxRows = 10,
  
  // Undo functionality
  enableUndo = true,
  undoTimeoutMs = 15000,
  undoThreshold = 20,
  
  // Accessibility
  helpText,
  helpTextEs,
  helpTextEn,
  
  // Status
  loading = false,
  success = false,
  
  // Cognitive load
  fieldPriority = 'optional',
  cognitiveWeight: _cognitiveWeight = 2,
  
  // Locale
  locale = 'es',
  
  // Standard props
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  required = false,
  placeholder,
  rows = minRows,
  ...props
}, _ref) => {
  // State management
  const [internalValue, setInternalValue] = useState<string>((value || defaultValue || '') as string)
  const [isFocused, setIsFocused] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [validationError, setValidationError] = useState<string>('')
  const [currentRows, setCurrentRows] = useState(rows)
  const [undoState, setUndoState] = useState<UndoState>({
    previousValue: '',
    timeout: null,
    isActive: false
  })
  
  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()
  const lastSavedValueRef = useRef<string>(internalValue)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayError = error || validationError
  const displaySolution = getDisplayText(solution, solutionEs, solutionEn, '')
  const displayHelpText = getDisplayText(helpText, helpTextEs, helpTextEn, '')
  
  // Character count calculations
  const characterCount = internalValue.length
  const isApproachingLimit = maxLength && characterCount >= (maxLength * characterCountThreshold)
  const isOverLimit = maxLength && characterCount > maxLength
  
  // Validation function
  const validateValue = useCallback((val: string): string => {
    for (const rule of validationRules) {
      if (!rule.test(val)) {
        return getDisplayText(
          undefined,
          rule.errorEs,
          rule.errorEn,
          rule.errorEs || 'Error de validación'
        )
      }
    }
    return ''
  }, [validationRules, getDisplayText])
  
  // Auto-resize functionality
  const updateRows = useCallback((textarea: HTMLTextAreaElement) => {
    if (!autoResize) return
    
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 20
    const newRows = Math.min(
      Math.max(
        Math.ceil(textarea.scrollHeight / lineHeight),
        minRows
      ),
      maxRows
    )
    
    setCurrentRows(newRows)
  }, [autoResize, minRows, maxRows])
  
  // Handle undo functionality
  const handleUndo = useCallback(() => {
    if (undoState.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      setInternalValue(undoState.previousValue)
      onChange?.({ target: { value: undoState.previousValue } } as React.ChangeEvent<HTMLTextAreaElement>)
      setUndoState({ previousValue: '', timeout: null, isActive: false })
    }
  }, [undoState, onChange])
  
  // Setup undo for significant changes
  const setupUndo = useCallback((newValue: string, oldValue: string) => {
    if (!enableUndo) return
    
    const charDifference = Math.abs(newValue.length - oldValue.length)
    if (charDifference >= undoThreshold) {
      // Clear existing timeout
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
      
      // Set up new undo state
      const timeout = setTimeout(() => {
        setUndoState(prev => ({ ...prev, isActive: false, timeout: null }))
      }, undoTimeoutMs)
      
      setUndoState({
        previousValue: oldValue,
        timeout,
        isActive: true
      })
    }
  }, [enableUndo, undoThreshold, undoTimeoutMs, undoState.timeout])
  
  // Handle input change
  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    const oldValue = internalValue
    
    // Update value
    setInternalValue(newValue)
    
    // Setup undo for significant changes
    setupUndo(newValue, oldValue)
    
    // Auto-resize
    if (autoResize && textareaRef.current) {
      updateRows(textareaRef.current)
    }
    
    // Progressive validation
    if (validateOnChange) {
      const errorMessage = validateValue(newValue)
      setValidationError(errorMessage)
    }
    
    // Auto-save setup
    if (autoSave && onAutoSave) {
      setAutoSaveStatus('idle')
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      
      autoSaveTimeoutRef.current = setTimeout(async () => {
        if (newValue !== lastSavedValueRef.current) {
          setAutoSaveStatus('saving')
          try {
            await onAutoSave(newValue)
            lastSavedValueRef.current = newValue
            setAutoSaveStatus('saved')
            // Clear saved status after 2 seconds
            setTimeout(() => setAutoSaveStatus('idle'), 2000)
          } catch {
            setAutoSaveStatus('error')
          }
        }
      }, autoSaveDelay)
    }
    
    // Call parent onChange
    onChange?.(event)
  }, [
    internalValue,
    autoResize,
    validateOnChange,
    autoSave,
    onAutoSave,
    autoSaveDelay,
    onChange,
    setupUndo,
    updateRows,
    validateValue
  ])
  
  // Handle blur
  const handleBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false)
    
    if (validateOnBlur) {
      const errorMessage = validateValue(event.target.value)
      setValidationError(errorMessage)
    }
    
    onBlur?.(event)
  }, [validateOnBlur, validateValue, onBlur])
  
  // Handle focus
  const handleFocus = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true)
    onFocus?.(event)
  }, [onFocus])
  
  // Effect: Sync external value changes
  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value as string)
    }
  }, [value, internalValue])
  
  // Effect: Initial auto-resize
  useEffect(() => {
    if (autoResize && textareaRef.current) {
      updateRows(textareaRef.current)
    }
  }, [autoResize, updateRows])
  
  // Cleanup undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoState.timeout) {
        clearTimeout(undoState.timeout)
      }
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [undoState.timeout])
  
  // Style classes based on BIRHAUS tokens
  const sizeClasses = {
    sm: 'text-sm px-2 py-1',
    md: 'text-base px-3 py-2',
    lg: 'text-lg px-4 py-3'
  }
  
  const variantClasses = {
    default: 'bg-white border border-gray-300',
    filled: 'bg-gray-50 border border-gray-200',
    minimal: 'bg-transparent border-b border-gray-300'
  }
  
  // Status colors
  const getStatusColor = () => {
    if (displayError) return 'border-red-300 focus:border-red-500'
    if (success) return 'border-green-300 focus:border-green-500'
    if (isFocused) return 'border-primary-500'
    return 'border-gray-300'
  }
  
  return (
    <div className={cn('birhaus-textarea', disabled && 'opacity-50')}>
      {/* Label */}
      {displayLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
          {fieldPriority === 'optional' && (
            <span className="text-gray-400 ml-1 text-xs">(opcional)</span>
          )}
        </label>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-sm text-gray-600 mb-2">
          {displayDescription}
        </p>
      )}
      
      {/* Textarea Container */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          rows={autoResize ? currentRows : rows}
          maxLength={maxLength}
          disabled={disabled || loading}
          required={required}
          placeholder={placeholder}
          className={cn(
            'w-full rounded-md transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
            'resize-none', // Controlled by autoResize
            safeAccess(sizeClasses, size, sizeClasses.md),
            safeAccess(variantClasses, variant, variantClasses.default),
            getStatusColor(),
            isOverLimit ? 'border-red-500 text-red-600' : '',
            disabled && 'cursor-not-allowed bg-gray-50'
          )}
          {...props}
        />
        
        {/* Status Icons */}
        <div className="absolute top-2 right-2 flex items-center space-x-1">
          {loading && (
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-primary-600 rounded-full" />
          )}
          {success && <CheckCircle2 className="h-4 w-4 text-green-500" />}
          {displayError && <AlertCircle className="h-4 w-4 text-red-500" />}
          {autoSaveStatus === 'saving' && (
            <div className="text-xs text-gray-500">Guardando...</div>
          )}
          {autoSaveStatus === 'saved' && (
            <div className="text-xs text-green-600">Guardado</div>
          )}
          {autoSaveStatus === 'error' && (
            <div className="text-xs text-red-600">Error</div>
          )}
        </div>
      </div>
      
      {/* Character Count */}
      {(showCharacterCount || maxLength) && (
        <div className="flex justify-between items-center mt-1">
          <div className="flex-1">
            {/* Error or Help Text */}
            {displayError ? (
              <div className="text-sm text-red-600">
                <div className="flex items-start space-x-1">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <div>{displayError}</div>
                    {displaySolution && (
                      <div className="text-xs text-gray-600 mt-1">
                        💡 {displaySolution}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : displayHelpText ? (
              <div className="text-sm text-gray-600 flex items-start space-x-1">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{displayHelpText}</span>
              </div>
            ) : null}
          </div>
          
          <div className={cn(
            'text-sm flex-shrink-0 ml-2',
            isOverLimit ? 'text-red-600 font-medium' :
            isApproachingLimit ? 'text-orange-600' :
            'text-gray-500'
          )}>
            {maxLength ? `${characterCount}/${maxLength}` : characterCount}
          </div>
        </div>
      )}
      
      {/* Undo Notification */}
      {undoState.isActive && (
        <div className="mt-2 bg-gray-800 text-white text-sm rounded-md p-3 flex items-center justify-between">
          <span>Texto modificado significativamente</span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Deshacer</span>
          </button>
        </div>
      )}
    </div>
  )
})

BirhausTextarea.displayName = 'BirhausTextarea'

// Default validation rules for common use cases
export const birhausTextareaValidationRules = {
  // Spanish-first validation rules
  required: (): ValidationRule => ({
    test: (value: string) => value.trim().length > 0,
    errorEs: 'Este campo es requerido',
    errorEn: 'This field is required',
    solutionEs: 'Por favor ingresa algún texto',
    solutionEn: 'Please enter some text'
  }),
  
  minLength: (min: number): ValidationRule => ({
    test: (value: string) => value.trim().length >= min,
    errorEs: `Debe tener al menos ${min} caracteres`,
    errorEn: `Must be at least ${min} characters`,
    solutionEs: `Agrega caracteres adicionales`,
    solutionEn: `Add additional characters`
  }),
  
  maxWords: (max: number): ValidationRule => ({
    test: (value: string) => value.trim().split(/\s+/).length <= max,
    errorEs: `Máximo ${max} palabras permitidas`,
    errorEn: `Maximum ${max} words allowed`,
    solutionEs: 'Reduce el texto para cumplir el límite',
    solutionEn: 'Reduce text to meet the limit'
  }),
  
  noSpecialChars: (): ValidationRule => ({
    test: (value: string) => /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s.,!?]+$/.test(value),
    errorEs: 'Solo se permiten letras, espacios y puntuación básica',
    errorEn: 'Only letters, spaces and basic punctuation allowed',
    solutionEs: 'Remueve caracteres especiales como @, #, $',
    solutionEn: 'Remove special characters like @, #, $'
  })
}

// Hook for enhanced textarea functionality
export const useBirhausTextarea = (initialValue: string = '') => {
  const [value, setValue] = useState(initialValue)
  const [history, setHistory] = useState<string[]>([initialValue])
  const [historyIndex, setHistoryIndex] = useState(0)
  
  const updateValue = useCallback((newValue: string) => {
    setValue(newValue)
    const newHistory = history.slice(0, historyIndex + 1)
    newHistory.push(newValue)
    setHistory(newHistory.slice(-10)) // Keep last 10 states
    setHistoryIndex(newHistory.length - 1)
  }, [history, historyIndex])
  
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1
      setHistoryIndex(newIndex)
      const value = safeArrayAccess(history, newIndex, '')
      setValue(value)
      return value
    }
    return null
  }, [history, historyIndex])
  
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1
      setHistoryIndex(newIndex)
      const value = safeArrayAccess(history, newIndex, '')
      setValue(value)
      return value
    }
    return null
  }, [history, historyIndex])
  
  const canUndo = historyIndex > 0
  const canRedo = historyIndex < history.length - 1
  
  return {
    value,
    setValue: updateValue,
    undo,
    redo,
    canUndo,
    canRedo,
    wordCount: value.trim().split(/\s+/).filter(word => word.length > 0).length,
    characterCount: value.length
  }
}